<template>
  <el-dialog v-loading="loading" class="order-dialog" :visible="$store.state.site.orderModal.visible && isMobilePay"
    :show-close="false" destroy-on-close fullscreen width="100%" @open="onOpen">
    <div class="order-dialog__content">
      <div class="order-dialog__header flex justify-center items-center relative">
        <div class="text-16 text-black">{{ $t('siteBuild.orderInfo') }}</div>
        <i class="el-icon-close text-16 absolute right-5" @click="handleClose" />
      </div>
      <div class="order-dialog__body">
        <div v-if="payInfo.code" class="logo-area flex mt-[20px] items-center slide-up">
          <img v-if="projectIcon" :src="projectIcon" class="app-logo" alt />
          <div v-if="!projectIcon" class="flex items-center justify-center app_logo default_app_logo">
            <img src="~/assets/img/default-app.png" alt />
          </div>
          <span class="ml-5">{{ projectName }}</span>
        </div>
        <div v-if="gameName && payInfo.code" class="game-name-area mb-[20px] slide-up">{{
          $t('siteBuild.header.userAccount') }}：{{ gameName }}</div>
        <div v-if="payInfo.total_amount" class="price-area pb-4 mb-4 mx-4 border-b slide-up">
          <div class="pay_tit">{{ $t('siteBuild.totalAmount') }}</div>
          <div class="pay_total_amount">{{ payInfo.currency }} {{ payInfo.total_amount }}</div>
          <div class="pay_sub_total">{{ $t('siteBuild.subTotal') }}：{{ payInfo.currency }} {{ payInfo.before_total_amount }}
          </div>
          <div v-if="payInfo.is_discount_flag" class="pay_sub_total mt-2">{{ $t('siteBuild.discountTip') }}：{{
            payInfo.discount_amount }}</div>
          <div v-if="payInfo && typeof payInfo.with_tax === 'boolean' && !payInfo.with_tax"
            class="pay_sub_total mt-[2px]">
            {{ $t('siteBuild.tax') }}：{{ payInfo.currency }} {{ payInfo.taxes_value ? payInfo.taxes_value.toFixed(2) :
            0.00}}
          </div>
        </div>
        <div v-if="goodsData.length" class="goods-area slide-up">
          <div v-for="good in goodsData" :key="`good_${good.id}`" class="good_detial">
            <div class="pb-4 mb-4 border-b">
              <div class="good_tit mb-2">{{ $t('siteBuild.buyGoods') }}</div>
              <div class="good_item flex">
                <img v-if="good.item_icon" :src="good.item_icon" class="w-[54px] h-[54px] rounded mr-4 good_logo" alt />
                <div class="flex-1 flex flex-col justify-center">
                  <div class="good_name mb-1">{{ getGoodsName(good.names?.lang_id) }}</div>
                  <div class="good_price flex items-center">
                    <div class="w-[120px] mr-6">{{ good.currency }} {{ Number(good.item_type) === 3 ? good.num : good.price }}</div>
                    <div v-if="Number(good.item_type) !== 3">x{{ good.num }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-if="activityGifts.length" class="gift-area">
            <div class="good_tit mb-2">{{ $t('siteBuild.gift') }}</div>
            <div v-for="(activity, index) in activityGifts" :key="`activity_${index}`" class="gift-activity">
              <div v-if="getActivityName(activity)" class="gift-activity__name">{{ getActivityName(activity) }}</div>
              <div v-for="(gift, idx) in activity.gifts" :key="`gift_${index}_${idx}`" class="gift-item">
                <img :src="gift.logo" class="gift-item__icon" alt />
                <span class="gift-item__name">{{ getGoodsName(gift.gift_goods_name) }}</span>
                <span class="gift-item__num">×{{ gift.num }}</span>
              </div>
            </div>
          </div>
        </div>
        <div v-if="payInfo?.merchant_vip?.customer_vip_growth" class="vip-box slide-up px-4">
          <div>{{ payInfo.merchant_vip?.customer_vip_level_name }}</div>
          <div class="mt-2">{{ $t('siteBuild.growth') }}：+{{ payInfo.merchant_vip?.customer_vip_growth }}</div>
          <div class="mt-2">{{ $t('siteBuild.points') }}：+{{ payInfo.merchant_vip?.customer_vip_points }}</div>
        </div>
        <div v-else-if="payInfo?.kop_vip?.customer_vip_growth" class="vip-box px-4 slide-up">
          <div>{{ payInfo?.kop_vip?.customer_vip_level_name }}</div>
          <div class="mt-2">{{ $t('siteBuild.growth') }}：+{{ payInfo.kop_vip?.customer_vip_growth }}</div>
          <div class="mt-2">{{ $t('siteBuild.points') }}：+{{ payInfo.kop_vip?.customer_vip_points }}</div>
        </div>
        <div v-else-if="showProjectVipInfo" class="vip-box px-4 slide-up">
          <div>{{ projectMemberLevelName }}</div>
          <div class="mt-2 flex items-center">
            <img :src="projectMember.growth_icon" alt class="w-[24px] h-[24px] mr-2">
            <span>{{ projectMemberGrowthName }}</span>：+{{ projectMember?.growth }}
          </div>
        </div>
      </div>
      <div class="handle-area">
        <div class="submit-btn" @click="handleToPay">Pay {{ payInfo.currency }} {{ payInfo.total_amount }}</div>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import { Dialog, Message } from 'element-ui'
import { eventTracker } from '~/utils'
import { DeviceEnum } from '~/enums/deviceEnum'
import { EventCodeEnums } from '~/enums/siteEnum'
import { ActivityTypeI18nKeyMap } from '~/enums/activityTypeEnum'

export default {
  name: 'OrderModal',
  components: {
    [Dialog.name]: Dialog
  },
  data() {
    return {
      loading: false,
      payInfo: {}
    }
  },
  computed: {
    isMobilePay() {
      return this.$store.state.site?.orderModal?.device === DeviceEnum.MOBILE
    },
    locale() {
      return this.$store.state.locale.locale || 'en'
    },
    project() {
      return this.$store.state.project || {}
    },
    projectIcon() {
      return this.project && this.project.info && this.project.info.project && this.project.info.project.icon ? this.project.info.project.icon : ''
    },
    projectName() {
      const names = this.project && this.project.info && this.project.info.project && this.project.info.project.names ? this.project.info.project.names : {}
      return names[this.locale] ? names[this.locale] : names.en ? names.en : ""
    },
    gameName() {
      return this.$store.state.user.siteUserInfo && this.$store.state.user.siteUserInfo.name || ''
    },
    goodsData() {
      return this.payInfo && this.payInfo.items ? this.payInfo.items : []
    },
    langIdData() {
      return this.$store.state.locale.langIdData || {}
    },
    activityGifts() {
      return this.payInfo?.activity_gifts || []
    },
    showProjectVipInfo() {
      return this.payInfo?.project_member?.growth && this.payInfo?.project_member?.switch
    },
    projectMember() {
      return this.payInfo?.project_member || {}
    },
    projectMemberLevelName() {
      return this.langIdData[this.projectMember.level_name] || this.projectMember.level_name
    },
    projectMemberGrowthName() {
      return this.langIdData[this.projectMember.growth_name] || this.projectMember.growth_name
    }
  },
  methods: {
    getGoodsName(name) {
      return this.langIdData[name] || name
    },
    getActivityName(activity) {
      const type = activity && activity.activity_type
      const key = ActivityTypeI18nKeyMap[type]
      if (key) return this.$t(key)
      return (activity && activity.activity_name) || ''
    },
    handleClose() {
      this.$store.commit('site/CLOSE_ORDER_MODAL')
    },
    async getOrderDetail() {
      const code = this.$store.state.site.orderModal.order
      this.loading = true
      const [err, res] = await this.$utils.to(this.$api.order.getOrderInfo({ code }))
      this.loading = false
      if (!err) {
        this.payInfo = res
        eventTracker(this, EventCodeEnums.ORDER_SHOW, code)
      } else {
        Message.error(err.message)
      }
    },
    onOpen() {
      this.payInfo = {}
      this.getOrderDetail()
    },
    handleToPay() {
      window.open(this.$store.state.site.orderModal.payUrl, '_self')
    }
  }
}
</script>

<style lang="less">
.order-dialog {
.orange-color {
  color: #ff001d;
}
  .slide-down {
    opacity: 0;
    animation: slideDown 0.24s linear forwards;
  }

  .slide-up {
    opacity: 0;
    animation: slideUp 0.24s linear forwards;
  }

  @keyframes slideUp {
    from {
      transform: translateY(100px);
      opacity: 0;
    }

    to {
      transform: translateY(0px);
      opacity: 1;
    }
  }

  @keyframes slideDown {
    from {
      transform: translateY(-100px);
      opacity: 0;
    }

    to {
      transform: translateY(0px);
      opacity: 1;
    }
  }

  .el-dialog {
    border-radius: 0;

    .el-dialog__header {
      display: none;
    }

    .el-dialog__body {
      padding: 0;
    }
  }

  &__header {
    line-height: 48px;
    border-bottom: 1px solid #dedede;
  }

  &__body {
    max-height: calc(100vh - 50px);
    padding-bottom: 88px;
    overflow: auto;
  }

  &__content {
    .logo-area {
      padding: 20px;

      .app-logo {
        width: 40px;
        height: 40px;
        border-radius: 100%;
      }
    }

    .game-name-area {
      font-family: PingFang SC;
      font-size: 14px;
      font-weight: normal;
      line-height: 22px;
      letter-spacing: 0;
      color: #18171c;
      padding: 8px 12px;
      border-radius: 2px;
      background-color: rgba(61, 70, 245, 0.1);
      animation-delay: 100ms;
      margin: 0 20px;
    }

    .price-area {
      font-family: PingFang SC;
      font-weight: normal;
      color: #18171c;
      animation-delay: 300ms;

      .pay_tit {
        font-weight: bold;
        font-size: 14px;
        line-height: 22px;
      }

      .pay_total_amount {
        font-weight: bold;
        font-size: 32px;
        line-height: 48px;
        color: @primary-1;
      }

      .pay_sub_total {
        font-size: 14px;
        line-height: 22px;
        opacity: 0.5;
      }
    }

    .goods-area {
      animation-delay: 500ms;
      padding: 0 20px;

      .good_tit {
        font-family: PingFang SC;
        font-size: 14px;
        font-weight: normal;
        line-height: 22px;
        color: #18171c;
      }

      .good_item {
        font-family: PingFang SC;
        letter-spacing: 0px;
        color: #18171c;

        img {
          border: 1px solid #ebeef1;
          object-fit: contain;
        }

        .good_name {
          font-size: 16px;
          font-weight: bold;
          line-height: 24px;
        }

        .good_price {
          font-size: 14px;
          line-height: 22px;
          opacity: 0.5;
        }
      }

      .default_good_logo {
        border: 1px solid #ebeef1;
        background: #ffffff;
      }

      .good_logo {
        border: 1px solid #ebeef1;
      }

      .gift-area {
        .gift-activity {
          background-color: #f7f8fa;
          border-radius: 8px;
          margin-bottom: 12px;
          overflow: hidden;

          &__name {
            padding: 10px 14px;
            font-size: 13px;
            font-weight: 600;
            line-height: 20px;
            color: #18171c;
            background-color: #eef0f4;
          }

          .gift-item {
            display: flex;
            align-items: center;
            padding: 10px 14px;
            border-bottom: 1px solid #f0f1f4;

            &:last-child {
              border-bottom: none;
            }

            &__icon {
              width: 40px;
              height: 40px;
              border-radius: 8px;
              margin-right: 12px;
              object-fit: cover;
              flex-shrink: 0;
            }

            &__name {
              flex: 1;
              min-width: 0;
              font-size: 14px;
              line-height: 22px;
              color: #18171c;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }

            &__num {
              font-size: 14px;
              font-weight: 600;
              line-height: 22px;
              color: #ff4d4f;
            }
          }
        }
      }
    }

    .handle-area {
      position: fixed;
      bottom: 0;
      left: 0;
      width: 100%;
      padding: 20px;
      border-top: 1px solid #dedede;
      background-color: white;

      .submit-btn {
        height: 48px;
        background-color: @primary-1;
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 2px;
        font-size: 16px;
      }
    }
  }
}
</style>
